import React from 'react';
import { Button as MuiButtonBase, CircularProgress } from '@mui/material';
import {
  ContentCopy,
  Visibility,
  VisibilityOff,
  Share,
  Delete,
  Edit,
  Add,
  Search,
  FilterList,
  Sort,
  Settings,
  Logout,
  Check,
  Close,
  Download,
  Upload,
  Refresh,
} from '@mui/icons-material';

// 아이콘 매핑
const iconMap = {
  copy: ContentCopy,
  eye: Visibility,
  eyeOff: VisibilityOff,
  share: Share,
  trash: Delete,
  delete: Delete,
  edit: Edit,
  add: Add,
  search: Search,
  filter: FilterList,
  sort: Sort,
  settings: Settings,
  logout: Logout,
  check: Check,
  x: Close,
  download: Download,
  upload: Upload,
  refresh: Refresh,
};

/**
 * Material UI v5 버튼 래퍼 컴포넌트
 * 기존 Button API와 호환되도록 설계
 */
function MuiButton({
  variant = 'primary',
  size = 'md',
  icon,
  iconOnly = false,
  disabled = false,
  loading = false,
  className = '',
  onClick,
  children,
  title,
  type = 'button',
  ...props
}) {
  // 기존 variant를 MUI variant와 color로 매핑
  const getMuiProps = () => {
    const baseProps = {
      disabled: disabled || loading,
      onClick,
      title,
      type,
      className,
    };

    // 크기 매핑
    const sizeMap = {
      sm: 'small',
      md: 'medium',
      lg: 'large',
    };
    baseProps.size = sizeMap[size] || 'medium';

    // variant와 color 매핑
    switch (variant) {
      case 'primary':
        return { ...baseProps, variant: 'contained', color: 'primary' };
      case 'secondary':
        return { ...baseProps, variant: 'contained', color: 'secondary' };
      case 'danger':
        return { ...baseProps, variant: 'contained', color: 'error' };
      case 'success':
        return { ...baseProps, variant: 'contained', color: 'success' };
      case 'outline-primary':
        return { ...baseProps, variant: 'outlined', color: 'primary' };
      case 'outline-secondary':
        return { ...baseProps, variant: 'outlined', color: 'secondary' };
      case 'outline-danger':
        return { ...baseProps, variant: 'outlined', color: 'error' };
      case 'ghost':
        return { ...baseProps, variant: 'text', color: 'inherit' };
      default:
        return { ...baseProps, variant: 'contained', color: 'primary' };
    }
  };

  // 아이콘 렌더링
  const renderIcon = () => {
    if (loading) {
      return <CircularProgress size={16} color="inherit" />;
    }

    if (icon) {
      // MUI 아이콘 컴포넌트 확인
      const IconComponent = iconMap[icon];
      if (IconComponent) {
        return <IconComponent fontSize="small" />;
      }

      // 이모지나 텍스트 아이콘
      if (typeof icon === 'string' && icon.length <= 2) {
        return <span>{icon}</span>;
      }

      return <span>{icon}</span>;
    }

    return null;
  };

  const muiProps = getMuiProps();
  const iconElement = renderIcon();

  // 아이콘만 있는 버튼
  if (iconOnly && iconElement) {
    return (
      <MuiButtonBase
        {...muiProps}
        {...props}
        sx={{
          minWidth: 'auto',
          padding: size === 'sm' ? '6px' : size === 'lg' ? '12px' : '8px',
          ...props.sx,
        }}
      >
        {iconElement}
      </MuiButtonBase>
    );
  }

  // 일반 버튼
  return (
    <MuiButtonBase
      {...muiProps}
      {...props}
      startIcon={iconElement}
    >
      {children}
    </MuiButtonBase>
  );
}

export default MuiButton;